import type * as React from "react";
import { useId } from "react";

type Common = { label?: string; hint?: string; error?: string };
export type InputProps = Common & React.InputHTMLAttributes<HTMLInputElement> & { as?: "input" };
export type TextareaProps = Common &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { as: "textarea" };

function Field({
  id,
  label,
  hint,
  error,
  children,
}: Common & { id: string; children: React.ReactNode }) {
  if (!label && !hint && !error) return <>{children}</>;
  return (
    <div className="pr-field">
      {label && (
        <label className="pr-label" htmlFor={id}>
          {label}
        </label>
      )}
      {children}
      {error ? (
        <span className="pr-help pr-help--error">{error}</span>
      ) : hint ? (
        <span className="pr-help">{hint}</span>
      ) : null}
    </div>
  );
}

/** Text input or textarea (`as="textarea"`), optionally wrapped as a full labelled field. */
export function Input(props: InputProps | TextareaProps) {
  const generated = useId();
  const { label, hint, error, className = "", ...rest } = props;
  const id = (rest as { id?: string }).id ?? generated;
  const cls = ["pr-input", error ? "pr-input--invalid" : "", className].filter(Boolean).join(" ");
  if (rest.as === "textarea") {
    const { as: _as, ...ta } = rest;
    return (
      <Field id={id} label={label} hint={hint} error={error}>
        <textarea id={id} className={cls} aria-invalid={error ? true : undefined} {...ta} />
      </Field>
    );
  }
  const { as: _as, ...input } = rest as InputProps;
  return (
    <Field id={id} label={label} hint={hint} error={error}>
      <input id={id} className={cls} aria-invalid={error ? true : undefined} {...input} />
    </Field>
  );
}
